import { useCallback } from "react";
import { getPeople, createPerson, updatePerson, movePerson, deletePerson } from "@/lib/api";
import { invalidateQuery, invalidateQueryPrefix, useQueryCore } from "@/hooks/useQueryCore";
import { generateUUID } from "@/lib/uuid";

const sortPeople = (data) =>
  (data || []).slice().sort((a, b) => {
    const nameA = (a.name || "").toLowerCase();
    const nameB = (b.name || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });

export function usePeople(filters = {}) {
  // Dexie key is only valid when filtering by a single family_id (the common case).
  // Other filter combos (district_id, church_id) don't have a dedicated Dexie entry.
  const dexieCacheKey = filters.family_id && Object.keys(filters).length === 1
    ? `people_fam_${filters.family_id}`
    : null;

  const query = useQueryCore({
    key: ["people", JSON.stringify(filters)],
    enabled: Object.keys(filters).length > 0,
    queryFn: () => getPeople(filters),
    sortFn: sortPeople,
    cacheKey: dexieCacheKey,
  });

  const { data, loading, error, refetch, setData } = query;

  const create = useCallback(async (payload) => {
    const tempId = generateUUID();

    setData((prev) => [
      ...(prev || []),
      { ...payload, id: tempId, _is_offline: true }
    ]);

    try {
      const result = await createPerson(payload);
      await refetch();
      invalidateQueryPrefix(["people"]);
      return result;
    } catch (err) {
      await refetch();
      throw err;
    }
  }, [refetch, setData]);

  const update = useCallback(async (id, updates) => {
    setData((prev) =>
      (prev || []).map((p) =>
        p.id === id ? { ...p, ...updates } : p
      )
    );

    try {
      const result = await updatePerson(id, updates);
      invalidateQueryPrefix(["people"]);
      invalidateQuery(["person-detail", id]);
      return result;
    } catch (err) {
      await refetch();
      throw err;
    }
  }, [refetch, setData]);

  const move = useCallback(async (personId, fromFamilyId, toFamilyId) => {
    setData((prev) =>
      (prev || []).filter((p) => p.id !== personId)
    );

    try {
      const result = await movePerson(personId, fromFamilyId, toFamilyId);
      invalidateQueryPrefix(["people"]);
      if (fromFamilyId) {
        invalidateQuery(["people", JSON.stringify({ family_id: fromFamilyId })]);
      }
      invalidateQuery(["people", JSON.stringify({ family_id: toFamilyId })]);
      return result;
    } catch (err) {
      await refetch();
      throw err;
    }
  }, [refetch, setData]);

  const remove = useCallback(async (id, familyId) => {
    setData((prev) =>
      (prev || []).filter((p) => p.id !== id)
    );

    try {
      await deletePerson(id, familyId);
      invalidateQueryPrefix(["people"]);
      if (familyId) {
        invalidateQuery(["people", JSON.stringify({ family_id: familyId })]);
      }
    } catch (err) {
      await refetch();
      throw err;
    }
  }, [refetch, setData]);

  return {
    people: data || [],
    loading,
    error,
    refetch,
    create,
    update,
    move,
    remove,
  };
}
